import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CheckSettings } from "@shared-core/data/cost-engine-validation-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  validateCheckSettingsDraft,
  type CheckSettingsField,
} from "@shared-core/domain/check-settings-validation";
import {
  formatCurrencyGbp,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Input } from "../app/components/primitives/input";
import { MoneyInput } from "../app/components/patterns/command-shell/CommandFields";
import { Textarea } from "../app/components/primitives/textarea";
import { Loader2, Settings2 } from "lucide-react";
import { SupersedeChecksPanel } from "../app/components/domain/manufacturing/SupersedeChecksPanel";

/**
 * Settings › Catalogue › Live costing › Check settings (design decision,
 * `04_handover_2026-09-12.md`; split out of the Status tab into its own tab,
 * 12 Sep): the admin parameters behind Matches / Small differences /
 * Mismatch, the same-direction repeat rule, the check window, the weekly
 * check budget and how rolling runs are paced — read-only display with an "Edit" affordance, same
 * audited-note pattern as Routing policy's "Choices (admin)".
 */
export function LiveCostingCheckSettingsPage() {
  return (
    <div className="space-y-4">
      <CheckSettingsCard />
      <SupersedeChecksPanel />
    </div>
  );
}

function CheckSettingsCard() {
  const adapter = useDataAdapter();
  const [settings, setSettings] = useState<CheckSettings | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CheckSettings | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverFieldErrors, setServerFieldErrors] = useState<
    Partial<Record<CheckSettingsField | "note", string>>
  >({});

  const load = useCallback(() => {
    setLoadError(false);
    adapter
      .getCheckSettings()
      .then(setSettings)
      .catch(() => setLoadError(true));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  function openEdit() {
    if (!settings) return;
    setDraft(settings);
    setNote("");
    setServerFieldErrors({});
    setEditing(true);
  }

  const clientFieldErrors = useMemo(
    () => (draft ? validateCheckSettingsDraft(draft) : {}),
    [draft],
  );
  const isDraftValid = Object.keys(clientFieldErrors).length === 0;
  // Client-side (pre-submission) errors take precedence when both exist for
  // the same field; server errors surface a business rule the client-side
  // check doesn't know about (e.g. a business cap the draft otherwise
  // passes locally).
  const fieldErrors: Partial<Record<CheckSettingsField, string>> = {
    ...serverFieldErrors,
    ...clientFieldErrors,
  };

  async function submit() {
    if (!draft || !isDraftValid) return;
    setSubmitting(true);
    setServerFieldErrors({});
    try {
      const updated = await adapter.updateCheckSettings({
        matchesTolerancePct: draft.matchesTolerancePct,
        mismatchLimitPct: draft.mismatchLimitPct,
        sameDirectionCount: draft.sameDirectionCount,
        sameDirectionWindow: draft.sameDirectionWindow,
        checkWindowWeeks: draft.checkWindowWeeks,
        weeklyCheckBudget: draft.weeklyCheckBudget,
        orderGuardPct: draft.orderGuardPct,
        orderGuardFloorPence: draft.orderGuardFloorPence,
        controlMintBudgetPerWeek: draft.controlMintBudgetPerWeek,
        lineFloorPence: draft.lineFloorPence,
        mintIntervalSeconds: draft.mintIntervalSeconds,
        runMintCap: draft.runMintCap,
        timeoutBackoffSeconds: draft.timeoutBackoffSeconds,
        note,
      });
      setSettings(updated);
      toast.success("Check settings updated");
      setEditing(false);
      setNote("");
    } catch (err) {
      const fieldErrorList = extractFieldErrors(err);
      if (fieldErrorList.length > 0) {
        const byField: Partial<Record<CheckSettingsField | "note", string>> =
          {};
        for (const fe of fieldErrorList) {
          byField[fe.field as CheckSettingsField | "note"] = fe.message;
        }
        setServerFieldErrors(byField);
      } else {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to update check settings",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const serverErrorCount = Object.keys(serverFieldErrors).length;

  if (loadError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings2 className="size-4" /> Check settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">
            Couldn&rsquo;t load check settings. Please try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings2 className="size-4" /> Check settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Settings2 className="size-4" /> Check settings
        </CardTitle>
        <Button variant="outline" size="sm" onClick={openEdit}>
          Edit
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 sm:grid-cols-2">
          <SettingRow
            label="Matches tolerance"
            value={`${settings.matchesTolerancePct}%`}
          />
          <SettingRow
            label="Mismatch (red) limit"
            value={`${settings.mismatchLimitPct}%`}
          />
          <SettingRow
            label="Same direction to count as a Mismatch"
            value={`${settings.sameDirectionCount} of the last ${settings.sameDirectionWindow} checks`}
          />
          <SettingRow
            label="Check window"
            value={`${settings.checkWindowWeeks} weeks`}
          />
          <SettingRow
            label="Weekly check budget"
            value={`${settings.weeklyCheckBudget} checks/week`}
          />
          <SettingRow
            label="Control-mint budget"
            value={`${settings.controlMintBudgetPerWeek} unforced mints/week`}
          />
          <SettingRow
            label="Line floor"
            value={pounds(settings.lineFloorPence)}
          />
        </dl>

        <div className="space-y-1 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Matches</span> — every
            check is within the Matches tolerance (
            {settings.matchesTolerancePct}%).
          </p>
          <p>
            <span className="font-medium text-foreground">
              Small differences
            </span>{" "}
            — some checks differ, each under the Mismatch limit (
            {settings.mismatchLimitPct}%), and not all the same direction.
          </p>
          <p>
            <span className="font-medium text-foreground">Mismatch</span> — the
            same direction on at least {settings.sameDirectionCount} of the last{" "}
            {settings.sameDirectionWindow} checks
            {settings.lineFloorPence > 0 ? (
              <>
                {" "}
                (counting only differences of {pounds(
                  settings.lineFloorPence,
                )}{" "}
                or more)
              </>
            ) : null}
            , or any single check over the Mismatch limit (
            {settings.mismatchLimitPct}%).
          </p>
          <p>
            {settings.lineFloorPence === 0 ? (
              <>
                The line floor is off — every line is classified on percentage
                alone, and every difference counts towards the same-direction
                rule.
              </>
            ) : (
              <>
                A line differing by less than {pounds(settings.lineFloorPence)}{" "}
                never trips a check, whatever the percentage says, and does not
                count towards the same-direction rule above. Its real difference
                is still recorded and shown on the pair.
              </>
            )}
          </p>
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-xs font-medium text-foreground">
            Rolling run pacing
          </p>
          <dl className="grid gap-3 sm:grid-cols-2">
            <SettingRow
              label="Mint interval"
              value={
                settings.mintIntervalSeconds > 0
                  ? `${settings.mintIntervalSeconds} s between estimate requests`
                  : "Off — no pacing"
              }
            />
            <SettingRow
              label="Per-run cap"
              value={`${settings.runMintCap} mints per run`}
            />
            <SettingRow
              label="Back-off after a Tharstern timeout"
              value={
                settings.timeoutBackoffSeconds > 0
                  ? `${settings.timeoutBackoffSeconds} s before the next request`
                  : "Off — no back-off"
              }
            />
          </dl>
          <p className="text-xs text-muted-foreground">
            Rolling validation runs every hour. Each run sends at most{" "}
            {settings.runMintCap} estimate requests to Tharstern
            {settings.mintIntervalSeconds > 0
              ? `, at least ${settings.mintIntervalSeconds} s apart`
              : ""}
            , until the weekly check budget ({settings.weeklyCheckBudget}) is
            spent. A price whose request times out is quarantined — no run sends
            it again until someone releases it on its item&rsquo;s Live costing
            panel.
          </p>
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-xs font-medium text-foreground">Order guard</p>
          <dl className="grid gap-3 sm:grid-cols-2">
            <SettingRow
              label="Order guard threshold"
              value={`${settings.orderGuardPct}%`}
            />
            <SettingRow
              label="Order guard floor"
              value={pounds(settings.orderGuardFloorPence)}
            />
          </dl>
          <p className="text-xs text-muted-foreground">
            Flags an order for review when the served price and
            Tharstern&rsquo;s cost differ by more than {settings.orderGuardPct}%{" "}
            <span className="font-medium text-foreground">and</span> more than{" "}
            {pounds(settings.orderGuardFloorPence)} — both must be exceeded, so
            a small absolute difference on a cheap line never trips it.
          </p>
        </div>

        <p className="text-xs text-muted-foreground">
          Changed by{" "}
          <span className="font-medium">{settings.lastChangedBy}</span> ·{" "}
          {formatDateTimeOrDash(settings.lastChangedAt)} &ldquo;
          {settings.lastChangedNote}&rdquo;
        </p>
      </CardContent>

      <AlertDialog
        open={editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(false);
            setNote("");
            setServerFieldErrors({});
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit check settings</AlertDialogTitle>
            <AlertDialogDescription>
              This changes how every future check is classified. A note is
              required.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {serverErrorCount > 0 && (
            <p role="alert" className="text-sm text-destructive">
              {serverErrorCount === 1
                ? "1 field needs attention below."
                : `${serverErrorCount} fields need attention below.`}
            </p>
          )}
          {draft && (
            <div className="grid gap-3 sm:grid-cols-2">
              <NumberField
                field="matchesTolerancePct"
                label="Matches tolerance (%)"
                value={draft.matchesTolerancePct}
                error={fieldErrors.matchesTolerancePct}
                onChange={(v) => setDraft({ ...draft, matchesTolerancePct: v })}
              />
              <NumberField
                field="mismatchLimitPct"
                label="Mismatch (red) limit (%)"
                value={draft.mismatchLimitPct}
                error={fieldErrors.mismatchLimitPct}
                onChange={(v) => setDraft({ ...draft, mismatchLimitPct: v })}
              />
              <NumberField
                field="sameDirectionCount"
                label="Same-direction count"
                value={draft.sameDirectionCount}
                error={fieldErrors.sameDirectionCount}
                onChange={(v) => setDraft({ ...draft, sameDirectionCount: v })}
              />
              <NumberField
                field="sameDirectionWindow"
                label="Same-direction window (checks)"
                value={draft.sameDirectionWindow}
                error={fieldErrors.sameDirectionWindow}
                onChange={(v) => setDraft({ ...draft, sameDirectionWindow: v })}
              />
              <NumberField
                field="checkWindowWeeks"
                label="Check window (weeks)"
                value={draft.checkWindowWeeks}
                error={fieldErrors.checkWindowWeeks}
                onChange={(v) => setDraft({ ...draft, checkWindowWeeks: v })}
              />
              <NumberField
                field="weeklyCheckBudget"
                label="Weekly check budget"
                value={draft.weeklyCheckBudget}
                error={fieldErrors.weeklyCheckBudget}
                onChange={(v) => setDraft({ ...draft, weeklyCheckBudget: v })}
              />
              <NumberField
                field="controlMintBudgetPerWeek"
                label="Control-mint budget (unforced mints/week)"
                value={draft.controlMintBudgetPerWeek}
                error={fieldErrors.controlMintBudgetPerWeek}
                onChange={(v) =>
                  setDraft({ ...draft, controlMintBudgetPerWeek: v })
                }
              />
              <MoneyField
                field="lineFloorPence"
                label="Line floor"
                valuePence={draft.lineFloorPence}
                error={fieldErrors.lineFloorPence}
                onChange={(v) => setDraft({ ...draft, lineFloorPence: v })}
              />
              <p className="col-span-full -mt-1 text-xs text-muted-foreground">
                A cost line differing by less than this never trips a check,
                whatever the percentage says — its real difference is still
                recorded. £0.00 turns the floor off.
              </p>
              <div className="col-span-full space-y-3 rounded-md border border-border p-3">
                <p className="text-xs font-medium text-foreground">
                  Rolling run pacing
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <NumberField
                    field="mintIntervalSeconds"
                    label="Mint interval (seconds)"
                    value={draft.mintIntervalSeconds}
                    error={fieldErrors.mintIntervalSeconds}
                    onChange={(v) =>
                      setDraft({ ...draft, mintIntervalSeconds: v })
                    }
                  />
                  <NumberField
                    field="runMintCap"
                    label="Per-run cap (mints)"
                    value={draft.runMintCap}
                    error={fieldErrors.runMintCap}
                    onChange={(v) => setDraft({ ...draft, runMintCap: v })}
                  />
                  <NumberField
                    field="timeoutBackoffSeconds"
                    label="Back-off after a Tharstern timeout (seconds)"
                    value={draft.timeoutBackoffSeconds}
                    error={fieldErrors.timeoutBackoffSeconds}
                    onChange={(v) =>
                      setDraft({ ...draft, timeoutBackoffSeconds: v })
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimum gap between Tharstern estimate requests in a rolling
                  run. 0 turns pacing off.
                </p>
                <p className="text-xs text-muted-foreground">
                  The per-run cap is the most estimate requests one hourly run
                  sends; the weekly check budget still bounds the week.
                </p>
                <p className="text-xs text-muted-foreground">
                  One hung request leaves Tharstern timing out every request for
                  up to an hour, so after a timeout the run waits this long
                  before its next one. 0 turns the back-off off.
                </p>
              </div>
              <div className="col-span-full space-y-3 rounded-md border border-border p-3">
                <p className="text-xs font-medium text-foreground">
                  Order guard
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <NumberField
                    field="orderGuardPct"
                    label="Order guard threshold (%)"
                    value={draft.orderGuardPct}
                    error={fieldErrors.orderGuardPct}
                    onChange={(v) => setDraft({ ...draft, orderGuardPct: v })}
                  />
                  <MoneyField
                    field="orderGuardFloorPence"
                    label="Order guard floor"
                    valuePence={draft.orderGuardFloorPence}
                    error={fieldErrors.orderGuardFloorPence}
                    onChange={(v) =>
                      setDraft({ ...draft, orderGuardFloorPence: v })
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Flag an order for review when the served price and
                  Tharstern&rsquo;s cost differ by more than this percentage{" "}
                  <span className="font-medium text-foreground">and</span> more
                  than this amount.
                </p>
              </div>
            </div>
          )}
          <Textarea
            placeholder="Note (required)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-invalid={serverFieldErrors.note != null}
            aria-describedby={
              serverFieldErrors.note ? "check-settings-note-error" : undefined
            }
          />
          {serverFieldErrors.note && (
            <p
              id="check-settings-note-error"
              className="text-xs text-destructive"
            >
              {serverFieldErrors.note}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!note.trim() || !isDraftValid || submitting}
              onClick={(e) => {
                // Radix's AlertDialogAction closes the dialog on click by
                // default; preventDefault keeps it open so a server-side
                // validation error (a business rule the client-side check
                // doesn't know about) can render next to the offending
                // field instead of the dialog silently vanishing.
                // `submit()` closes it itself on success.
                e.preventDefault();
                void submit();
              }}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}

/** Pounds and pence as staff read them; the settings hold pence. */
function pounds(pence: number): string {
  return formatCurrencyGbp(pence / 100, 2);
}

/** A money setting: pounds in the box, pence in the draft. An empty or
 *  unreadable box is NaN in the draft, so the draft validation flags the
 *  field and Confirm stays disabled. */
function MoneyField({
  field,
  label,
  valuePence,
  error,
  onChange,
}: {
  field: CheckSettingsField;
  label: string;
  valuePence: number;
  error?: string;
  onChange: (pence: number) => void;
}) {
  const id = `check-settings-${field}`;
  return (
    <div className="space-y-1 text-xs">
      <label htmlFor={id} className="text-muted-foreground">
        {label}
      </label>
      <MoneyInput
        id={id}
        value={Number.isNaN(valuePence) ? null : valuePence}
        disabled={false}
        errors={error ? [{ field, code: "invalid", message: error }] : []}
        onChange={(pence) => onChange(typeof pence === "number" ? pence : NaN)}
      />
      {error && (
        <p id={`${id}-error`} className="text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

function NumberField({
  field,
  label,
  value,
  error,
  onChange,
}: {
  field: CheckSettingsField;
  label: string;
  value: number;
  error?: string;
  onChange: (value: number) => void;
}) {
  const errorId = `check-settings-${field}-error`;
  return (
    <label className="space-y-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <Input
        aria-invalid={error != null}
        aria-describedby={error ? errorId : undefined}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {error && (
        <p id={errorId} className="text-destructive">
          {error}
        </p>
      )}
    </label>
  );
}
